import { z } from 'zod';
import { AnnotationType, annotationSourceSchema, annotationTypeSchema } from '../enums.js';
/**
 * Rectangle en coordonnées **normalisées** dans `[0,1]`, relatives aux dimensions de l'image.
 *
 * C'est le point important : une bbox posée sur un rendu 720p doit s'afficher exactement au
 * même endroit sur le rendu 1080p ou sur la source 4K. Stocker des pixels lierait l'annotation
 * à la variante HLS sur laquelle elle a été créée, ce qui la rendrait fausse partout ailleurs.
 */
export const normalizedBoxSchema = z
    .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0).max(1),
    height: z.number().min(0).max(1),
})
    .refine((b) => b.x + b.width <= 1.0001 && b.y + b.height <= 1.0001, {
    message: 'Le rectangle déborde des limites de l’image',
});
const annotationBaseSchema = z.object({
    labelId: z.string().cuid2(),
    note: z.string().trim().max(2000).optional(),
});
/** Un instant précis. */
export const markerAnnotationSchema = annotationBaseSchema.extend({
    type: z.literal(AnnotationType.MARKER),
    startMs: z.number().int().nonnegative(),
});
/** Un segment temporel. */
export const intervalAnnotationSchema = annotationBaseSchema
    .extend({
    type: z.literal(AnnotationType.INTERVAL),
    startMs: z.number().int().nonnegative(),
    endMs: z.number().int().nonnegative(),
})
    .refine((a) => a.endMs > a.startMs, {
    message: 'La fin doit être postérieure au début',
    path: ['endMs'],
});
/** Un rectangle sur une frame donnée. */
export const bboxAnnotationSchema = annotationBaseSchema.extend({
    type: z.literal(AnnotationType.BBOX),
    startMs: z.number().int().nonnegative(),
    box: normalizedBoxSchema,
});
export const createAnnotationSchema = z.discriminatedUnion('type', [
    markerAnnotationSchema,
    bboxAnnotationSchema,
    // Note : `intervalAnnotationSchema` porte un `.refine`, ce qui le sort du type accepté par
    // `discriminatedUnion`. Il est validé séparément dans `parseCreateAnnotation`.
]);
/**
 * Valide une création d'annotation, tous types confondus.
 * Contourne la limite de `z.discriminatedUnion` face aux schémas raffinés.
 */
export function parseCreateAnnotation(input) {
    const shape = z.object({ type: annotationTypeSchema }).safeParse(input);
    if (!shape.success) {
        throw new Error('Type d’annotation manquant ou invalide');
    }
    switch (shape.data.type) {
        case AnnotationType.MARKER:
            return markerAnnotationSchema.parse(input);
        case AnnotationType.INTERVAL:
            return intervalAnnotationSchema.parse(input);
        case AnnotationType.BBOX:
            return bboxAnnotationSchema.parse(input);
    }
}
export const updateAnnotationSchema = z.object({
    labelId: z.string().cuid2().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
    startMs: z.number().int().nonnegative().optional(),
    endMs: z.number().int().nonnegative().nullable().optional(),
    box: normalizedBoxSchema.nullable().optional(),
});
/**
 * Validation d'une suggestion du modèle (ADR-0013, incrément 2 : « un modèle propose, l'humain
 * valide »). Accepter une prédiction la fait passer en `HUMAN_VERIFIED`. On peut la **corriger** au
 * passage — recadrer la boîte, changer le label — les deux champs sont donc facultatifs.
 */
export const acceptAnnotationSchema = z.object({
    labelId: z.string().cuid2().optional(),
    box: normalizedBoxSchema.optional(),
});
/**
 * Acceptation en lot des suggestions (« tout accepter au-dessus du seuil »). `startMs` restreint à
 * une frame ; absent, porte sur toute la vidéo. `minConfidence` ne retient que les prédictions assez
 * sûres — le curseur de confiance de l'UI.
 */
export const acceptSuggestionsSchema = z.object({
    minConfidence: z.number().min(0).max(1).default(0),
    startMs: z.number().int().nonnegative().optional(),
});
export const acceptSuggestionsResultSchema = z.object({
    accepted: z.number().int().nonnegative(),
});
export const annotationSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    videoId: z.string(),
    type: annotationTypeSchema,
    source: annotationSourceSchema,
    labelId: z.string(),
    labelName: z.string(),
    labelColor: z.string(),
    note: z.string().nullable(),
    startMs: z.number().int().nonnegative(),
    endMs: z.number().int().nonnegative().nullable(),
    box: normalizedBoxSchema.nullable(),
    /** Confiance du modèle dans `[0,1]`, `null` pour une annotation humaine (ADR-0006). */
    confidence: z.number().min(0).max(1).nullable(),
    modelName: z.string().nullable(),
    modelVersion: z.string().nullable(),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    reviewedById: z.string().nullable(),
    reviewedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Formats d'export (FR-28). `coco` produit un jeu de données directement exploitable pour
 * entraîner un modèle de détection — c'est la raison d'être de la v1 selon ADR-0006.
 */
export const exportFormatSchema = z.enum(['json', 'csv', 'coco']);
export const exportAnnotationsSchema = z.object({
    format: exportFormatSchema,
    videoIds: z.array(z.string().cuid2()).min(1).max(500).optional(),
    fieldSessionId: z.string().cuid2().optional(),
    campaignId: z.string().cuid2().optional(),
    /** N'exporter que les bounding boxes — seul type utilisable en détection d'objets. */
    bboxOnly: z.boolean().default(false),
});
//# sourceMappingURL=annotation.js.map